import { useState, useCallback } from "react";
import { AuthSignUpConfirmAccountSubPage, AuthMFASubtitle } from "../authBarStyling";
import { Auth } from "aws-amplify";
import { userModelSelector } from "../../../state/selectors/userModel";
import { accessTokenSelector } from "../../../state/selectors/accessToken";
import { useRecoilValue } from "recoil";
import ContinueBtn from "../../btn/continueBtn/continueBtn";
import { signUpLoginUser } from "../../../utils/auth/signUpLoginUser";
import { callApi } from "../../../utils/api/api";
import { ICredentials } from "@aws-amplify/core";
import CustomVerificationInput from "./verificationInput";
import { useToast } from "../../toast/ToastContext";

interface SignUpVerificationPopupProps {
    username: string;
    signUpPassword: string;
    setPageName: (pageName: string) => void;
    codeDeliveryDetails: { AttributeName?: string; DeliveryMedium?: string; Destination?: string };
    pageName: string;
    countdown: number;
    activateCountdown: (active: boolean) => void;
    resendNotReady: boolean;
    handleKeyboardDismiss: () => void;
}

const SignUpVerificationPopup: React.FC<SignUpVerificationPopupProps> = ({
    username,
    signUpPassword,
    setPageName,
    codeDeliveryDetails,
    pageName,
    countdown,
    activateCountdown,
    resendNotReady,
    handleKeyboardDismiss,
}) => {
    const [mfaInvalid, setMfaInvalid] = useState(false);
    const [mfaCode, setMfaCode] = useState("");
    const userModel = useRecoilValue(userModelSelector);
    const accessToken = useRecoilValue(accessTokenSelector);
    const [isContinueBtnLoading, setIsContinueBtnLoading] = useState(false);
    const { showToast } = useToast();

    const triggerResend = useCallback(async (): Promise<void> => {
        if (resendNotReady) return;
        activateCountdown(true);
        await Auth.resendSignUp(username)
            .then((res) => {})
            .catch((err) => {
                if (err.code === "CodeMismatchException") {
                    setMfaInvalid(true);
                    showToast("You have entered the wrong code, please try again", "error");
                } else if (err.code === "LimitExceededException") {
                    showToast("You exceeded your reset attempts, please try again later", "error");
                }
            });
    }, [activateCountdown, resendNotReady, username, showToast]);

    if (pageName !== "SignUpVerificationPopup") return null;

    const confirmSignUpAfterVerificationCode = async (code: string): Promise<void> => {
        setIsContinueBtnLoading(true);
        await Auth.confirmSignUp(username, code)
            .then(async (res) => {
                if (signUpPassword) {
                    await Auth.signIn(username, signUpPassword).then(async (user) => {
                        let creds = await Auth.currentUserCredentials();
                        updateIdentityId(creds);
                        await signUpLoginUser(user);
                        setPageName("AddProfilePhotoPopup");
                    });
                } else {
                    setPageName("PasswordPopup");
                }
            })
            .catch((error) => {
                console.log(error);
                if (error.code === "CodeMismatchException") {
                    setMfaInvalid(true);
                    setIsContinueBtnLoading(false);
                    showToast("You have entered the wrong code, please try again", "error");
                }
            });
    };

    const updateIdentityId = async (creds: ICredentials) => {
        if (userModel) {
            if (userModel.identityID === null) {
                await callApi("post", `${process.env.REACT_APP_ENV}-anystay-user`, `user/identityid`, {
                    headers: {
                        Authorization: accessToken,
                    },
                    body: {
                        identityID: creds.identityId,
                    },
                });
            }
        }
    };

    const handleComplete = async (code: string) => {
        if (code.length === 6) {
            await confirmSignUpAfterVerificationCode(code);
        }
    };

    const handleChange = (code: string) => {
        setMfaInvalid(false);
        setMfaCode(code);
    };

    return (
        <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AuthSignUpConfirmAccountSubPage style={{ height: "auto" }}>
                <CustomVerificationInput
                    value={mfaCode}
                    onChange={handleChange}
                    onComplete={handleComplete}
                    invalid={mfaInvalid}
                    onBlur={handleKeyboardDismiss}
                />
                <AuthMFASubtitle style={{ marginBottom: "24px" }}>{`We sent a 6 digit code to ${codeDeliveryDetails ? codeDeliveryDetails.Destination : ""}`}</AuthMFASubtitle>
                <ContinueBtn
                    background={"black"}
                    style={{ marginTop: "0" }}
                    continueType="button"
                    onClick={triggerResend}
                    disabled={resendNotReady || isContinueBtnLoading}
                    isLoading={isContinueBtnLoading}
                >
                    Resend code {!resendNotReady ? "" : `(${countdown})`}
                </ContinueBtn>
            </AuthSignUpConfirmAccountSubPage>
        </div>
    );
};

export default SignUpVerificationPopup;
